import EmailIcon from '@mui/icons-material/Email';
import EventIcon from '@mui/icons-material/Event';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import { Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { useClientFields } from '../../../sic-lib/api/hooks/useClientFields';
import { useCreateContact } from '../../../sic-lib/api/hooks/useContact';
import { DEFAULT_LIST_ID, SYSTEM_PROP_ID } from '../../../sic-lib/defaults';
import { GenericReducer, genericReducer } from '../../../sic-lib/reducers/genericReducer';
import {
    CreateContactForm,
    buildCreateContact,
    createContactFormInitialState,
} from '../../../sic-lib/types/Contact';
import {
    ContactAddress,
    ContactField,
    FormFieldCreateContact,
    buildFormFieldCreateContact,
} from '../../../sic-lib/types/ContactField';
import { hasPopulatedFieldValue } from '../../../sic-lib/utils/functionalFilters';
import {
    mapFormFieldToCreateContactField,
    mapReplaceIfMatchedFieldId,
} from '../../../sic-lib/utils/functionalMappers';
import { groupClientFieldsByValidationType } from '../../../sic-lib/utils/functionalReducers';
import { requestContacts } from '../../../store/conversationGrid';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import AxiosErrorAlert from '../../Shared/Alerts/AxiosErrorAlert';
import ButtonLoading from '../../Shared/ButtonLoading';
import Loader from '../../Shared/Loader';
import ContactFormConsents from './ContactFormConsents/ContactFormConsents';
import ContactFormFastField from './ContactFormFastField/ContactFormFastField';
import ContactFormNamesField from './ContactFormFastField/ContactFormNamesField';
import ContactFormFastFieldAddress from './ContactFormFastFieldAddress/ContactFormFastFieldAddress';
import ContactFormFieldDate from './ContactFormFieldDate/ContactFormFieldDate';

interface Props {
    isOpen: boolean;
    onCloseHandler: () => void;
}

export default function DialogCreateContact({ isOpen, onCloseHandler }: Props) {
    const { classes } = useStyles();
    const dispatch = useAppDispatch();
    const history = useHistory();

    const dialogRef = React.useRef<HTMLDivElement | null>(null);

    const {
        mutate: createContact,
        isPending: isLoading,
        isSuccess,
        isError,
        error: formErrors,
    } = useCreateContact();

    const { data: clientFields, isPending: isLoadingClientFields } = useClientFields();

    const listId = useAppSelector(state => state.conversationGrid?.listId || DEFAULT_LIST_ID);

    const [formState, formDispatch] = React.useReducer<GenericReducer<CreateContactForm>>(
        genericReducer,
        createContactFormInitialState,
    );

    React.useEffect(() => {
        const groupedClientFields = groupClientFieldsByValidationType(clientFields || [], false);
        formDispatch({
            address: groupedClientFields.address.map(
                field =>
                    buildFormFieldCreateContact(field) as FormFieldCreateContact<ContactAddress>,
            ),
            date: groupedClientFields.date.map(
                field =>
                    buildFormFieldCreateContact(field) as FormFieldCreateContact<string | null>,
            ),
            dollar: groupedClientFields.dollar.map(
                field =>
                    buildFormFieldCreateContact(field) as FormFieldCreateContact<string | null>,
            ),
            email: groupedClientFields.email.map(
                field =>
                    buildFormFieldCreateContact(field) as FormFieldCreateContact<string | null>,
            ),
            other: groupedClientFields.other.map(
                field =>
                    buildFormFieldCreateContact(field) as FormFieldCreateContact<string | null>,
            ),
            none: groupedClientFields.none.map(
                field =>
                    buildFormFieldCreateContact(field) as FormFieldCreateContact<string | null>,
            ),
            phone: groupedClientFields.phone.map(
                field =>
                    buildFormFieldCreateContact(field) as FormFieldCreateContact<string | null>,
            ),
            year: groupedClientFields.year.map(
                field =>
                    buildFormFieldCreateContact(field) as FormFieldCreateContact<string | null>,
            ),
        });
    }, [clientFields]);

    const handleChangeField = (key: keyof CreateContactForm, field: FormFieldCreateContact) => {
        const nextState = (formState[key] as ContactField[]).map(mapReplaceIfMatchedFieldId(field));
        formDispatch({ [key]: nextState });
    };

    const handleSubmit = async () => {
        const request = buildCreateContact({
            firstName: formState.firstName,
            lastName: formState.lastName,
            pPolicy: formState.pPolicy,
            consent: formState.consent,
            terms: formState.terms,
            values: [
                ...formState.address,
                ...formState.date,
                ...formState.email,
                ...formState.phone,
            ]
                .filter(hasPopulatedFieldValue)
                .map(mapFormFieldToCreateContactField),
        });

        createContact(request, {
            onSuccess: data => {
                //only add contact to conversation grid if showing "All Contacts"
                listId === DEFAULT_LIST_ID && dispatch(requestContacts());
                history.push(`/contacts/${data.contactId}/details`);
                onCloseHandler();
            },
        });
    };

    return (
        <Dialog open={isOpen} aria-labelledby="dialog_create_contact">
            <DialogTitle id="dialog_create_contact">Create Contact</DialogTitle>
            <Loader isLoading={isLoadingClientFields}>
                <DialogContent dividers ref={dialogRef}>
                    <Grid container direction="column" spacing={2}>
                        <Grid item container direction="row" spacing={2} flexWrap="nowrap">
                            <Grid item>
                                <PersonIcon sx={{ mt: 1 }} />
                            </Grid>
                            <Grid item container direction="row" spacing={1}>
                                <Grid item xs={6}>
                                    <ContactFormNamesField
                                        fieldName="firstName"
                                        fieldValue={formState.firstName}
                                        label="First name"
                                        size="small"
                                        onChangeHandler={formDispatch}
                                        fullWidth
                                        formErrors={formErrors}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <ContactFormNamesField
                                        fieldName="lastName"
                                        fieldValue={formState.lastName}
                                        label="Last name"
                                        size="small"
                                        onChangeHandler={formDispatch}
                                        fullWidth
                                        formErrors={formErrors}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item container direction="row" spacing={2} flexWrap="nowrap">
                            <Grid item>
                                <PhoneIcon sx={{ mt: 1 }} />
                            </Grid>
                            <Grid item container direction="row" spacing={1}>
                                {formState.phone.length > 0 &&
                                    formState.phone.map(fld => (
                                        <Grid key={fld.fieldId} item xs={6}>
                                            <ContactFormFastField
                                                field={fld as FormFieldCreateContact<string>}
                                                fieldKey="phone"
                                                name={fld.label}
                                                label={fld.label}
                                                size="small"
                                                onChangeHandler={handleChangeField}
                                                formErrors={formErrors}
                                                fullWidth
                                            />
                                        </Grid>
                                    ))}
                            </Grid>
                        </Grid>
                        <Grid item container direction="row" spacing={2} flexWrap="nowrap">
                            <Grid item>
                                <EmailIcon sx={{ mt: 1 }} />
                            </Grid>
                            <Grid item container direction="row" spacing={1}>
                                {formState.email.length > 0 &&
                                    formState.email.map(fld => (
                                        <Grid key={fld.fieldId} item xs={6}>
                                            <ContactFormFastField
                                                field={fld as FormFieldCreateContact<string>}
                                                fieldKey="email"
                                                name={fld.label}
                                                label={fld.label}
                                                size="small"
                                                onChangeHandler={handleChangeField}
                                                formErrors={formErrors}
                                                fullWidth
                                            />
                                        </Grid>
                                    ))}
                            </Grid>
                        </Grid>
                        <Grid item container direction="row" spacing={2} flexWrap="nowrap">
                            <Grid item>
                                <HomeWorkIcon sx={{ mt: 1 }} />
                            </Grid>
                            <Grid item container direction="row" spacing={1}>
                                {formState.address.length > 0 &&
                                    formState.address.map(fld => (
                                        <Grid
                                            item
                                            container
                                            direction="row"
                                            spacing={1}
                                            key={fld.fieldId}
                                        >
                                            <Grid item xs={12}>
                                                <Typography component="legend">
                                                    {fld.label}
                                                </Typography>
                                            </Grid>
                                            <ContactFormFastFieldAddress
                                                field={
                                                    fld as FormFieldCreateContact<ContactAddress>
                                                }
                                                size="small"
                                                onChangeHandler={handleChangeField}
                                            />
                                        </Grid>
                                    ))}
                            </Grid>
                        </Grid>
                        <Grid item container direction="row" spacing={2} flexWrap="nowrap">
                            <Grid item>
                                <EventIcon sx={{ mt: 1 }} />
                            </Grid>
                            <Grid item container direction="row" spacing={2}>
                                {formState.date.length > 0 &&
                                    formState.date.map(
                                        fld =>
                                            fld.fieldId > SYSTEM_PROP_ID && (
                                                <Grid key={fld.fieldId} item xs={6}>
                                                    <ContactFormFieldDate
                                                        field={fld}
                                                        onChangeHandler={handleChangeField}
                                                        error={false}
                                                        helperText={''}
                                                    />
                                                </Grid>
                                            ),
                                    )}
                            </Grid>
                        </Grid>
                        <Grid item container direction="row">
                            <Grid item xs={true}>
                                <ContactFormConsents
                                    pPolicy={formState.pPolicy}
                                    consent={formState.consent}
                                    terms={formState.terms}
                                    dialogRef={dialogRef}
                                    formDispatch={formDispatch}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Loader>
            <DialogActions>
                <Grid container direction="column" spacing={1}>
                    {!!formErrors && (
                        <Grid item>
                            <AxiosErrorAlert
                                error={formErrors}
                                prefix="Please correct the following errors: "
                            />
                        </Grid>
                    )}
                    <Grid
                        item
                        container
                        direction="row"
                        alignItems="center"
                        justifyContent="flex-end"
                        spacing={2}
                    >
                        <Grid item>
                            <Button className={classes.btnCancel} onClick={onCloseHandler}>
                                Cancel
                            </Button>
                        </Grid>
                        <Grid item>
                            <ButtonLoading
                                isLoading={isLoading}
                                disabled={isLoading}
                                isSuccess={isSuccess}
                                isError={isError}
                                onClick={handleSubmit}
                                color="primary"
                                variant="contained"
                                successLabel="Created"
                            >
                                Create
                            </ButtonLoading>
                        </Grid>
                    </Grid>
                </Grid>
            </DialogActions>
        </Dialog>
    );
}

const useStyles = makeStyles()((theme: Theme) => ({
    iconWrapper: {},
    iconWrapperAddress: {},
    inputWrapper: {},
    buttonWrapper: {},
    btnCancel: {
        color: theme.palette.btnDefault.main,
        '&:hover': {
            backgroundColor: theme.palette.btnDefault.light,
        },
    },
}));
